import { Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";
import { ChartCard } from "../../../components/charts/ChartCard";
import { CATEGORICAL, chartSx } from "../../../components/charts/chartTheme";
import { AttendanceByGrade } from "../types";

// Past four sections there are no more validated colours; extra sections share a neutral grey.
const OVERFLOW_COLOR = "#94a3b8";

type AttendanceByGradeChartProps = {
  data: AttendanceByGrade[];
};

export const AttendanceByGradeChart = ({
  data,
}: AttendanceByGradeChartProps) => {
  // Flatten data for chart
  const chartData = useMemo(
    () =>
      data.map((item) => {
        const flattened: Record<string, string | number> = { grade: item.grade };
        item.sections.forEach(({ name, total }) => {
          flattened[name] = total;
        });
        return flattened;
      }),
    [data]
  );

  // Sections sorted by name so a section keeps the same colour on every grade and every day.
  const sections = useMemo(
    () => Array.from(new Set(data.flatMap((item) => item.sections.map((s) => s.name)))).sort(),
    [data]
  );

  const totalAbsentees = useMemo(
    () => data.reduce((sum, item) => sum + item.sections.reduce((s, sec) => s + sec.total, 0), 0),
    [data]
  );

  return (
    <Box sx={{ width: "100%", height: "100%", minWidth: 0 }}>
      <ChartCard title="Today's Absentees by Grade & Section" headline={totalAbsentees} headlineCaption="students absent">
        <BarChart
          height={320}
          dataset={chartData}
          xAxis={[{ scaleType: "band", dataKey: "grade", disableTicks: true, categoryGapRatio: 0.3, barGapRatio: 0.15 }]}
          yAxis={[{ width: 30, disableLine: true, disableTicks: true, tickMinStep: 1 }]}
          series={sections.map((section, i) => ({
            dataKey: section,
            label: section,
            color: CATEGORICAL[i] ?? OVERFLOW_COLOR,
          }))}
          // Value labels stay on: two of the section colours are low-contrast on white.
          barLabel="value"
          borderRadius={4}
          grid={{ horizontal: true }}
          margin={{ top: 16, right: 8, bottom: 0, left: 0 }}
          slotProps={{ legend: { position: { vertical: "top", horizontal: "end" } } }}
          sx={chartSx}
        />
      </ChartCard>
    </Box>
  );
};
